import { formatMoney } from '../../lib/formatters.ts'
import type { ForecastLine, ForecastVersion, RankedActions } from '../../api/cashReport'
import { isYesNoCashQuestion, openCashCardModel } from './sorenOpenCashCard.js'
import { shapeOwnerPayAnswer } from './shapeOwnerPayAnswer.js'

export type ShapedCashAnswer = {
  mode: 'yesno' | 'open'
  text: string
  yesNo: string
  amount: string | null
  date: string | null
  reason: string
  confidence: string
  version_id: string
  computed_at: string | null
  lead?: string
  dueOut?: string
  week0Close?: string
  lowest?: string
  lowestWeek?: string | null
  help?: string[]
  sourceLines?: ForecastLine[]
  offerFollowUp?: boolean
}

function weekClose(week: { close?: { amount?: number } | null; close_cents?: number } | undefined): number | null {
  if (!week) return null
  if (week.close && Number.isFinite(week.close.amount)) return Number(week.close.amount)
  if (week.close_cents != null) return Number(week.close_cents) / 100
  return null
}

export { shapeTajPayAnswer, shapeOwnerPayAnswer } from './shapeOwnerPayAnswer.js'

export function shapeCashAnswerFromVersion(
  question: string,
  version: ForecastVersion,
  actions?: RankedActions | null,
): ShapedCashAnswer {
  const q = String(question || '').toLowerCase()
  const weeks = version.payload?.weeks || []
  const thisWeek = weeks[0]
  const nextWeek = weeks[1]
  const thisAmt = weekClose(thisWeek)
  const thisFmt = thisAmt == null ? null : formatMoney(thisAmt)
  const confidence = String(version.confidence || 'unknown')
  const versionId = String(version.id || '')
  const computedAt = version.computed_at || null
  const yesNoOnly = isYesNoCashQuestion(question)

  const ownerPay = /when will.+\spay/i.test(question || '') || /taj/.test(q)
  if (!yesNoOnly && !ownerPay) {
    const open = openCashCardModel(version, actions || version.payload?.actions)
    return {
      mode: 'open',
      text: [open.lead, `Due out: ${open.dueOut}`, `This week closes at ${open.week0Close}`, `Lowest point: ${open.lowest}`, `Confidence ${open.confidence}.`].join(' '),
      yesNo: '',
      amount: open.expected,
      date: open.lowestWeek,
      reason: open.lead,
      confidence: open.confidence,
      version_id: versionId,
      computed_at: computedAt,
      lead: open.lead,
      dueOut: open.dueOut,
      week0Close: open.week0Close,
      lowest: open.lowest,
      lowestWeek: open.lowestWeek,
      help: open.help,
    }
  }

  let yesNo = 'Yes.'
  let amount = thisFmt
  let date = thisWeek?.week_start || null
  let reason = version.payload?.verdict?.lowest_line
    || version.payload?.verdict?.line
    || 'See the Cash Report sources.'
  let sourceLines: ForecastLine[] | undefined
  let offerFollowUp = false

  if (yesNoOnly || /short/.test(q)) {
    const thisShort = !!(thisWeek && (thisWeek.below_zero || thisWeek.below_cushion))
    const nextShort = !!(nextWeek && (nextWeek.below_zero || nextWeek.below_cushion))
    yesNo = thisShort || nextShort ? 'Yes.' : 'No.'
    reason = thisShort || nextShort
      ? 'This week or next closes below the cushion.'
      : 'This week and next stay above the cushion.'
  } else if (ownerPay) {
    const owner = shapeOwnerPayAnswer(version, question)
    yesNo = owner.yesNo
    amount = owner.amount
    date = owner.date
    reason = owner.reason
    sourceLines = owner.sourceLines
    offerFollowUp = owner.offerFollowUp
  }

  const text = ownerPay
    ? [reason, yesNo, offerFollowUp ? 'I can draft a follow-up' : null].filter(Boolean).join('\n\n')
    : [yesNo, amount, date ? `Date ${date}.` : null, reason, `Confidence ${confidence}.`]
      .filter(Boolean)
      .join(' ')
  return {
    mode: 'yesno',
    text, yesNo, amount, date, reason, confidence,
    version_id: versionId,
    computed_at: computedAt,
    sourceLines,
    offerFollowUp,
  }
}
